/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get, sortBy } from 'lodash';
import { format } from 'date-fns';

import { Mapper, PresenterType } from '../../types';
import { getPeriodDisplayLabel } from '../../../../../components/recurrence/utils';
import { flatByAttribute, groupDropdownExtraInfo, personDropdownExtraInfo } from '../../utils';
import { PeriodOptions, StopRepeatingOptions, DATE_FORMAT, Layout } from '../../constants';

import auditsQuery from '@/graphql/new_object/dropdown_options/AuditOptions.gql';
import controlsQuery from '@/graphql/new_object/dropdown_options/ControlOptions.gql';
import programsQuery from '@/graphql/new_object/dropdown_options/ProgramOptions.gql';
import tagsQuery from '@/graphql/new_object/dropdown_options/TagOptions.gql';
import { UserRole } from '@/constants/Users';

export default {
  title: {
    presenter: PresenterType.InputFormPresenter,
  },
  slug: {
    presenter: PresenterType.InputFormPresenter,
    overrideAttributes: {
      mandatory: false,
      validation: [
        { validator: 'COMPLEX', meta: ['UniquenessChecker'] },
        { validator: 'MAX_LENGTH', meta: ['250'] },
      ],
    },
  },
  description: {
    presenter: PresenterType.RichTextFormPresenter,
  },
  audit: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      optionsQuery: auditsQuery,
      dropdownOptionsParser: data =>
        sortBy(
          get(data, 'dropdownOptions.edges', []).map(edge => ({
            value: edge.node.id,
            label: edge.node.title.value,
            selected: false,
          })),
          ['label']
        ),
      editValueParser: data =>
        sortBy(
          get(data, 'value.edges', []).map(edge => ({
            value: edge.node.id,
            label: edge.node.title.value,
            selected: false,
          })),
          ['label']
        ),
      submissionDataParser: flatByAttribute('value'),
    },
  },
  control: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      optionsQuery: controlsQuery,
      dropdownOptionsParser: data => {
        return sortBy(
          get(data, 'dropdownOptions.edges', []).map(edge => ({
            // TODO: refactor query to include dbId
            value: edge.node.id,
            label: edge.node.title.value,
            selected: false,
          })),
          ['label']
        );
      },
      editValueParser: data => {
        const node = get(data, 'value');
        return {
          value: node.id,
          label: node.title.value,
        };
      },
      submissionDataParser: flatByAttribute('value'),
    },
  },
  program: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      optionsQuery: programsQuery,
      dropdownOptionsParser: data => {
        return sortBy(
          get(data, 'dropdownOptions.edges', []).map(edge => ({
            // TODO: refactor query to include dbId
            value: edge.node.id,
            label: edge.node.title.value,
            selected: false,
          })),
          ['label']
        );
      },
      editValueParser: data => {
        const node = get(data, 'value');
        return {
          value: node.id,
          label: node.title.value,
        };
      },
      submissionDataParser: flatByAttribute('value'),
    },
  },
  tags: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      optionsQuery: tagsQuery,
      dropdownOptionsParser: data =>
        get(data, 'dropdownOptions.edges', []).map(edge => ({
          value: edge.node.id,
          label: edge.node.title.value,
        })),
      editValueParser: data =>
        get(data, 'value.edges', []).map(edge => ({
          value: edge.node.id,
          label: edge.node.title.value,
        })),
      submissionDataParser: flatByAttribute('label'),
    },
    renderInfo: {
      presenterAttributes: {
        multiple: true,
        taggable: true,
      },
    },
  },
  status: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        allowEmpty: false,
      },
      formGroupAttributes: {
        required: false,
      },
    },
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      initialValue: { label: 'Open', value: 'Open' },
      dropdownOptionsParser: data => {
        const val = get(data, 'dropdownOptions.edges', null);
        return {
          value: val,
          label: val,
        };
      },
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  design: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  operationally: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  kind: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  networkZone: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  documents: {
    presenter: PresenterType.InputFormPresenter,
    renderInfo: {
      presenterAttributes: {
        readonly: true,
      },
    },
  },
  owners: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  ownersGroup: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
    extraInfo: {
      ...groupDropdownExtraInfo,
    },
  },
  people: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  contact: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  secondaryContact: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  relatedRequesters: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      ...personDropdownExtraInfo,
      initialValue: getters => [
        {
          id: `${getters.currentUser.__typename}:${getters.currentUser.dbId}`,
          label: getters.currentUser.sorTitle.value,
          role: getters.currentUser.userRole.value,
          value: getters.currentUser.email.value,
        },
      ],
    },
    renderInfo: {
      presenterAttributes: {
        multiple: true,
        allowEmpty: true,
      },
    },
  },
  relatedAssignees: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
        allowEmpty: true,
      },
    },
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  notifyAssignee: {
    presenter: PresenterType.CheckboxFormPresenter,
    overrideAttributes: {
      displayName: 'Notify via email',
    },
  },
  reviewers: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
    extraInfo: {
      ...personDropdownExtraInfo,
      editValueParser: data => {
        return get(data, 'value.edges', []).map(edge => ({
          value: edge.node.person.value.email.value,
          label: edge.node.person.value.name.value || edge.node.person.value.email.value,
          role: edge.node.person.value.userRole.value,
          id: edge.node.person.value.id,
          $isDisabled: edge.node.person.value.userRole.value === UserRole.Reader,
        }));
      },
    },
  },
  relatedVerifiers: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
    extraInfo: {
      ...personDropdownExtraInfo,
    },
  },
  relatedObjects: {
    presenter: PresenterType.DropdownFormPresenter,
    renderInfo: Layout.Columns.Half,
  },
  riskRating: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: flatByAttribute('value'),
      editValueParser: data => {
        const val = get(data, 'value', null);
        return {
          value: val,
          label: val,
        };
      },
    },
  },
  notes: {
    presenter: PresenterType.RichTextFormPresenter,
  },
  test: {
    presenter: PresenterType.RichTextFormPresenter,
  },
  period: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser(option) {
        if (option.value === PeriodOptions.DoesNotRepeat) {
          return undefined;
        }
        return flatByAttribute('value')(option);
      },
      initialValue: {
        label: getPeriodDisplayLabel(PeriodOptions.Weekly),
        value: PeriodOptions.Weekly,
      },
    },
    renderInfo: {
      presenterAttributes: {
        allowEmpty: false,
      },
    },
  },
  repeatOption: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser(option) {
        if (option.value === PeriodOptions.DoesNotRepeat) {
          return undefined;
        }
        return flatByAttribute('value')(option);
      },
      initialValue: {
        label: PeriodOptions.DoesNotRepeat,
        value: PeriodOptions.DoesNotRepeat,
      },
    },
    renderInfo: {
      presenterAttributes: {
        allowEmpty: false,
      },
    },
  },
  repeatInterval: {
    presenter: PresenterType.NumberSelectorFormPresenter,
    extraInfo: {
      initialValue: '1',
      submissionDataParser: val => +val,
    },
  },
  days: {
    presenter: PresenterType.DaySelectorFormPresenter,
    extraInfo: {
      initialValue: [1],
    },
  },
  repeatOn: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser(option) {
        return flatByAttribute('value')(option);
      },
    },
    renderInfo: {
      presenterAttributes: {
        allowEmpty: false,
      },
    },
  },
  startDate: {
    presenter: PresenterType.DateFormPresenter,
    extraInfo: {
      initialValue: format(new Date(), DATE_FORMAT),
    },
  },
  lastDayOfMonth: {
    presenter: PresenterType.CheckboxFormPresenter,
  },
  endDateMessage: {
    presenter: PresenterType.InfoMessageFormPresenter,
    extraInfo: {
      submissionDataParser: () => undefined,
    },
  },
  endDate: {
    presenter: PresenterType.DateFormPresenter,
  },
  dueIn: {
    presenter: PresenterType.NumberSelectorFormPresenter,
    extraInfo: {
      initialValue: '1',
      submissionDataParser: val => +val,
    },
  },
  stopRepeating: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      submissionDataParser: () => undefined,
      initialValue: {
        label: StopRepeatingOptions.Never,
        value: StopRepeatingOptions.Never,
      },
    },
    renderInfo: {
      presenterAttributes: {
        allowEmpty: false,
      },
    },
  },
  stopOn: {
    presenter: PresenterType.DateFormPresenter,
  },
  repeatTimes: {
    presenter: PresenterType.NumberSelectorFormPresenter,
    extraInfo: {
      initialValue: '1',
      submissionDataParser: val => +val,
    },
  },
  scheduleMessage: {
    presenter: PresenterType.InfoMessageFormPresenter,
    extraInfo: {
      submissionDataParser: () => undefined,
    },
  },
  relatedCreators: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      ...personDropdownExtraInfo,
      initialValue: getters => [
        {
          id: `${getters.currentUser.__typename}:${getters.currentUser.dbId}`,
          label: getters.currentUser.sorTitle.value,
          role: getters.currentUser.userRole.value,
          value: getters.currentUser.email.value,
        },
      ],
    },
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
  },
  relatedAssessors: {
    presenter: PresenterType.DropdownFormPresenter,
    extraInfo: {
      ...personDropdownExtraInfo,
    },
    renderInfo: {
      presenterAttributes: {
        multiple: true,
      },
    },
  },
  notifyAssessor: {
    presenter: PresenterType.CheckboxFormPresenter,
  },
  testPlan: {
    presenter: PresenterType.RichTextFormPresenter,
  },
  url: {
    presenter: PresenterType.InputFormPresenter,
  },
  referenceUrl: {
    presenter: PresenterType.InputFormPresenter,
  },
} as Mapper;
